// NarrativeCV — 中文自述生成英文 CV.
//
// The target user is a Chinese student in the UK who often has no English CV
// at all. They fill this Chinese form; the cv_from_narrative backend turns it
// into an English UK CV PDF (one LLM call → shared render → generate-pdf).
//
// A structured form, deliberately not a free-text box: a free-text prompt
// invites the model to invent, and a fabricated employer on a CV is the one
// unforgivable error. Every field maps to a known slot in the backend prompt.

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { useJobRunner } from '../lib/useJobRunner';
import { useZh, useProductReady } from '../lib/useProduct';

interface ExperienceRow { org: string; role: string; period: string; detail_zh: string; }
interface ProjectRow { title: string; detail_zh: string; }

const BLANK_EXP: ExperienceRow = { org: '', role: '', period: '', detail_zh: '' };
const BLANK_PROJ: ProjectRow = { title: '', detail_zh: '' };

const field: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  border: '1px solid var(--line)', background: 'var(--surface)',
  color: 'var(--ink)', fontSize: 13, boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = { fontSize: 12, color: 'var(--ink2)', marginBottom: 4, display: 'block' };

function Labeled({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={labelStyle}>
        {label}
        {hint && <span style={{ color: 'var(--ink3)', marginLeft: 6 }}>{hint}</span>}
      </label>
      {children}
    </div>
  );
}

export default function NarrativeCV() {
  const zh = useZh();
  const productReady = useProductReady();
  const [nameZh, setNameZh] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [school, setSchool] = useState('');
  const [major, setMajor] = useState('');
  const [degree, setDegree] = useState('');
  const [gradDate, setGradDate] = useState('');
  const [skills, setSkills] = useState('');
  const [summaryZh, setSummaryZh] = useState('');
  const [experiences, setExperiences] = useState<ExperienceRow[]>([{ ...BLANK_EXP }]);
  const [projects, setProjects] = useState<ProjectRow[]>([]);


  // No reattach: this is a fresh-generate flow, and reattach's list-poll would
  // fire repeatedly and adopt/lose the job around completion.
  const runner = useJobRunner('cv-from-narrative');

  // Latch the result into local state the moment the >>CV_PDF marker appears in
  // the stream. The runner's `log` comes from the SSE stream, which closes and
  // empties once the job leaves 'running' — so reading pdfName off runner.log
  // at render time misses it. Capturing it in an effect makes the result
  // survive the stream closing.
  const [result, setResult] = useState<{ pdf: string; cjkWarn: boolean } | null>(null);
  useEffect(() => {
    const m = runner.log.match(/>>CV_PDF:(\{.*\})/);
    if (!m) return;
    try {
      const pdf = JSON.parse(m[1]).pdf as string;
      if (pdf) setResult({ pdf, cjkWarn: /still contains \d+ Chinese characters/.test(runner.log) });
    } catch { /* marker not yet fully streamed */ }
  }, [runner.log]);

  // Experience is deliberately NOT required. The target user is often a
  // first-year or a career-changer with no internships at all — requiring one
  // locked out exactly the people who most need a CV generated for them. A
  // project, or just education plus a self-description, is enough to produce
  // something real.
  const canSubmit =
    (nameZh || nameEn) && school && major &&
    (experiences.some((e) => e.org || e.detail_zh) ||
     projects.some((p) => p.title || p.detail_zh) ||
     summaryZh.trim().length > 0) &&
    !runner.isRunning;

  const submit = () => {
    setResult(null); // clear any previous result before a fresh run
    const payload = {
      name_zh: nameZh, name_en: nameEn, email, phone, location,
      target_role: targetRole, school, major, degree, grad_date: gradDate,
      skills, summary_zh: summaryZh,
      experiences: experiences.filter((e) => e.org || e.role || e.detail_zh),
      projects: projects.filter((p) => p.title || p.detail_zh),
    };
    runner.startJob('cv_from_narrative', payload);
  };

  const setExp = (i: number, patch: Partial<ExperienceRow>) =>
    setExperiences((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const setProj = (i: number, patch: Partial<ProjectRow>) =>
    setProjects((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  // UK-CN-only surface. With the product flag off, this route doesn't exist —
  // but only once we know which product this is. Redirecting before /api/session
  // answers bounced every direct link to 生成简历, the paid differentiator.
  if (!productReady) return null;
  if (!zh) return <Navigate to="/matches" replace />;

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="生成简历"
        title="中文自述 → 英文 CV"
        sub="用中文填写你的经历，自动生成一份给英国雇主看的英文简历 PDF"
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 380px)', gap: 20, alignItems: 'start' }}>
        {/* 表单 */}
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>基本信息</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Labeled label="中文姓名"><input style={field} value={nameZh} onChange={(e) => setNameZh(e.target.value)} placeholder="李伟" /></Labeled>
              <Labeled label="英文姓名" hint="没有可留空"><input style={field} value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="Li Wei" /></Labeled>
              <Labeled label="邮箱"><input style={field} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="liwei@example.com" /></Labeled>
              <Labeled label="电话"><input style={field} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+44 7700 900123" /></Labeled>
              <Labeled label="所在城市"><input style={field} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="London, UK" /></Labeled>
              <Labeled label="目标岗位" hint="想找什么工作"><input style={field} value={targetRole} onChange={(e) => setTargetRole(e.target.value)} placeholder="投行分析师 / Investment Banking Analyst" /></Labeled>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>教育背景</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Labeled label="学校"><input style={field} value={school} onChange={(e) => setSchool(e.target.value)} placeholder="伦敦政治经济学院" /></Labeled>
              <Labeled label="专业"><input style={field} value={major} onChange={(e) => setMajor(e.target.value)} placeholder="金融学" /></Labeled>
              <Labeled label="学位"><input style={field} value={degree} onChange={(e) => setDegree(e.target.value)} placeholder="硕士 / 本科" /></Labeled>
              <Labeled label="毕业时间"><input style={field} value={gradDate} onChange={(e) => setGradDate(e.target.value)} placeholder="2026年9月" /></Labeled>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="eyebrow" style={{ marginBottom: 0 }}>实习 / 工作经历</div>
              <button className="btn ghost" onClick={() => setExperiences((xs) => [...xs, { ...BLANK_EXP }])}>＋ 添加一段</button>
            </div>
            {experiences.map((exp, i) => (
              <div key={i} style={{ padding: 12, border: '1px solid var(--line)', borderRadius: 8, marginBottom: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <input style={field} value={exp.org} onChange={(e) => setExp(i, { org: e.target.value })} placeholder="公司（如 德勤）" />
                  <input style={field} value={exp.role} onChange={(e) => setExp(i, { role: e.target.value })} placeholder="职位（如 审计实习生）" />
                  <input style={field} value={exp.period} onChange={(e) => setExp(i, { period: e.target.value })} placeholder="时间（如 2025年6-8月）" />
                </div>
                <textarea
                  style={{ ...field, minHeight: 60, resize: 'vertical' }}
                  value={exp.detail_zh}
                  onChange={(e) => setExp(i, { detail_zh: e.target.value })}
                  placeholder="用中文描述你做了什么（如实填写，会翻译成专业英文）"
                />
                {experiences.length > 1 && (
                  <button className="btn ghost" style={{ marginTop: 6, fontSize: 12 }} onClick={() => setExperiences((xs) => xs.filter((_, j) => j !== i))}>删除</button>
                )}
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="eyebrow" style={{ marginBottom: 0 }}>项目经历 <span style={{ color: 'var(--ink3)' }}>（选填）</span></div>
              <button className="btn ghost" onClick={() => setProjects((xs) => [...xs, { ...BLANK_PROJ }])}>＋ 添加一段</button>
            </div>
            {projects.length === 0 && <div className="page-sub">课程项目、毕业设计、个人作品等，没有可跳过</div>}
            {projects.map((proj, i) => (
              <div key={i} style={{ padding: 12, border: '1px solid var(--line)', borderRadius: 8, marginBottom: 10 }}>
                <input style={{ ...field, marginBottom: 8 }} value={proj.title} onChange={(e) => setProj(i, { title: e.target.value })} placeholder="项目名称" />
                <textarea style={{ ...field, minHeight: 50, resize: 'vertical' }} value={proj.detail_zh} onChange={(e) => setProj(i, { detail_zh: e.target.value })} placeholder="用中文描述项目内容" />
                <button className="btn ghost" style={{ marginTop: 6, fontSize: 12 }} onClick={() => setProjects((xs) => xs.filter((_, j) => j !== i))}>删除</button>
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>技能与自述</div>
            <Labeled label="技能" hint="逗号分隔">
              <input style={field} value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="Python, Excel, Bloomberg终端, 财务建模" />
            </Labeled>
            <Labeled label="一句话自我介绍" hint="选填，会润色成英文摘要">
              <textarea style={{ ...field, minHeight: 60, resize: 'vertical' }} value={summaryZh} onChange={(e) => setSummaryZh(e.target.value)} placeholder="我是即将毕业的金融硕士，有两段金融行业实习经历……" />
            </Labeled>
          </div>
        </div>

        {/* 生成面板 (sticky) */}
        <div style={{ position: 'sticky', top: 20 }}>
          <div className="card">
            <button
              className="btn primary"
              style={{ width: '100%', opacity: canSubmit ? 1 : 0.5 }}
              disabled={!canSubmit}
              onClick={submit}
            >
              {runner.isRunning ? '生成中…（约 1 分钟）' : '生成英文 CV'}
            </button>

            {/* Queue full / daily allowance used up. Without this the click was
                a silent no-op and the page looked broken. */}
            {runner.startError && (
              <div
                style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 6, fontSize: 13, lineHeight: 1.6,
                  background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.35)', color: '#7c4a03',
                }}
              >
                {runner.startError}
              </div>
            )}

            {/* Result is latched into local state, so it survives the SSE
                stream closing after completion (that empties runner.log). */}
            {result ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ color: 'var(--good, #2e7d32)', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>✓ 生成完成</div>
                {result.cjkWarn && (
                  <div className="page-sub" style={{ marginBottom: 8, color: 'var(--warn, #b26a00)' }}>
                    ⚠ 检测到部分内容可能没翻译完全，请下载后检查。
                  </div>
                )}
                {/* Backend mirrors the PDF into the tenant's reports/ dir;
                    /api/report serves it with path-traversal protection. */}
                <a className="btn" style={{ width: '100%', textAlign: 'center', display: 'block' }}
                   href={`/api/report?file=${encodeURIComponent('reports/' + result.pdf)}`}>
                  下载 PDF
                </a>
                <div className="page-sub" style={{ marginTop: 10, lineHeight: 1.6 }}>
                  下一步：去「职位板」找岗位，用 CV Writer 针对具体岗位定制这份 CV。
                </div>
              </div>
            ) : runner.isRunning ? (
              <div style={{ marginTop: 12 }}>
                {/* Queued and running look identical from isRunning alone, and
                    on a small self-hosted GPU the wait can be long. Saying
                    "正在生成…" while the job has not started yet reads as a
                    hang; naming the queue sets the right expectation. */}
                <div className="page-sub" style={{ marginBottom: 6 }}>
                  {runner.status === 'pending' ? '排队中…' : '正在生成…'}
                </div>
                {runner.status === 'pending' && (
                  <div className="page-sub" style={{ marginBottom: 6, lineHeight: 1.6 }}>
                    算力有限，前面还有任务在跑。这个页面可以关掉，回来还能看到进度。
                  </div>
                )}
                <pre style={{
                  fontSize: 11, color: 'var(--ink3)', maxHeight: 180, overflow: 'auto',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
                }}>{runner.log.slice(-800) || (runner.status === 'pending' ? '等待空闲槽位…' : '启动中…')}</pre>
              </div>
            ) : runner.status === 'failed' ? (
              <div style={{ marginTop: 12, color: 'var(--bad, #c62828)', fontSize: 13 }}>
                生成失败{runner.exitCode != null ? `（退出码 ${runner.exitCode}）` : ''}。请稍后重试。
                {runner.log && (
                  <pre style={{ fontSize: 11, marginTop: 6, maxHeight: 120, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                    {runner.log.slice(-400)}
                  </pre>
                )}
              </div>
            ) : (
              <div className="page-sub" style={{ marginTop: 12, lineHeight: 1.6 }}>
                填好左边的表单后点这里。系统会把你的中文经历翻译成专业英文，排版成一份适合英国求职的 CV。
                <br /><br />
                <strong>只翻译，不编造</strong>——不会给你添加没写的经历或数字。
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
